/**
 * BaseHeadline — the primary headline component used throughout the project.
 *
 * Props:
 *  headline          — the main headline text                                       (required)
 *  tag               — HTML heading tag: "h1"…"h6"; each has a preset responsive
 *                      font size (h1 = largest, h6 = smallest)                      (default: "h2")
 *  headlineClassProps — additional Tailwind classes applied to the heading element   (optional)
 *
 *  label             — small uppercase label rendered above the headline via
 *                      <BaseLabel>                                                   (optional)
 *  labelColor        — color of the label: "primary" (blue) | "secondary" (orange)  (default: "primary")
 *  labelClassProps   — additional Tailwind classes forwarded to <BaseLabel>          (optional)
 *
 *  text              — body copy rendered below the headline via <BaseText>;
 *                      supports HTML (e.g. <strong> tags)                            (optional)
 *  textClassProps    — additional Tailwind classes forwarded to <BaseText>           (optional)
 *
 *  width             — Tailwind width class for the wrapper div                     (default: "w-full")
 *  classProps        — additional Tailwind classes for the wrapper div               (optional)
 *  center            — if true, centers all children and text                       (default: false)
 */

import BaseLabel from "./BaseLabel";
import BaseText from "./BaseText";

type TagType = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
type LabelColor = "primary" | "secondary";

type BaseHeadlineType = {
  headline?: string;
  tag?: TagType;
  headlineClassProps?: string;
  label?: string;
  labelColor?: LabelColor;
  labelClassProps?: string;
  text?: string;
  textClassProps?: string;
  width?: string;
  classProps?: string;
  center?: boolean;
};

const tagSizeMap: Record<TagType, string> = {
  h1: "text-4xl sm:text-6xl",
  h2: "text-3xl sm:text-5xl",
  h3: "text-2xl sm:text-4xl",
  h4: "text-xl sm:text-3xl",
  h5: "text-lg sm:text-2xl",
  h6: "text-base sm:text-xl",
};

export default function BaseHeadline({
  headline,
  tag: Tag = "h2",
  headlineClassProps,
  label,
  labelColor = "primary",
  labelClassProps,
  text,
  textClassProps,
  width,
  classProps,
  center,
}: BaseHeadlineType) {
  return (
    <div className={`flex flex-col h-fit ${width ?? "w-full"} ${classProps ?? ""} ${center ? "items-center text-center" : "items-start"}`}>
      {label && (
        <BaseLabel label={label} color={labelColor} classProps={labelClassProps + " pb-4"} />
      )}
      <Tag className={`font-semibold tracking-tight font-text pb-4 ${tagSizeMap[Tag]} ${headlineClassProps ?? ""}`}>
        {headline}
      </Tag>
      {text && (
        <BaseText text={text} classProps={textClassProps} padding="py-0" />
      )}
    </div>
  );
}
